import React from 'react'
import { EnhancedDrawer, Icon } from '@adc/ui-core'
import classNames from 'classnames/bind'
import { track } from '@adc/analytics'
import type { primitives as BlueprintPrimitivesTypes } from '@adc/blueprint'
import { NavigationMenuItem } from './types'
import { getMenuItemLinkName, getVisibleMenuLinks } from './constants'
import smartSaleLogo from '../../../img/smartsale-logo.svg'
import auctionLogo from '../../../img/auction-logo-mobile.svg'
import styles from './styles.scss'
import headerStyles from '../styles.scss'

const cx = classNames.bind(styles)
const hcx = classNames.bind(headerStyles)

interface NavigationDrawerProps {
  user:
    | BlueprintPrimitivesTypes.AuthenticatedAuctionUser
    | { logged_in?: boolean; first_name?: string }
  menuItems: NavigationMenuItem[]
  formattedNotificationCount?: string
  isOpen: boolean
  onClose: () => void
  elmId?: string
  // When true, use the orange-dot indicator UI; when false, keep the legacy
  // numbered notification badge.
  indicatorsEnabled?: boolean
}

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  user,
  menuItems,
  formattedNotificationCount,
  isOpen,
  onClose,
  elmId = 'nav_drawer',
  indicatorsEnabled = false,
}) => {
  return (
    <EnhancedDrawer
      open={isOpen}
      onClose={onClose}
      mode='sidebar'
      mobileLayout='right-panel'
      closeOnBackdropClick
      className={cx('navigation-drawer')}
      elmId={elmId}
      headerContent={
        user.logged_in && (
          <div className={cx('drawer-title')}>
            Welcome, {user.first_name || 'User'}
          </div>
        )
      }
    >
      <div className={cx('drawer-menu')}>
        {(() => {
          const trackingContext = {
            linkSource: 'drawer' as const,
            visibleHeaderLinks: [],
            visibleDropdownLinks: getVisibleMenuLinks(menuItems),
          }
          return menuItems.map(item => {
            const handleClick = (e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              const linkName = getMenuItemLinkName(item)

              track('navigation-click', { linkName, ...trackingContext })
              onClose()

              if (item.onClick) {
                item.onClick(e)
              } else if (item.url && item.url !== '#') {
                window.location.href = item.url
              }
            }

            if (item.isDivider) {
              return (
                <div key={item.id} className={cx('menu-divider-wrapper')}>
                  <hr className={cx('menu-divider')} />
                </div>
              )
            }

            if (item.isSmartSaleLogo) {
              return (
                <a
                  key={item.id}
                  href={item.url}
                  onClick={handleClick}
                  className={cx('menu-item', 'sell-item')}
                  data-elm-id={item.elmId}
                >
                  <img
                    src={smartSaleLogo}
                    alt='Smart Sale'
                    className={cx('smartsale-logo')}
                  />
                  <span className={cx('menu-label')}>{item.title}</span>
                </a>
              )
            }

            if (item.isAuctionLogo) {
              return (
                <a
                  key={item.id}
                  href={item.url}
                  onClick={handleClick}
                  className={cx('menu-item', 'sell-item')}
                  data-elm-id={item.elmId}
                >
                  <img
                    src={auctionLogo}
                    alt='Auction.com'
                    className={cx('smartsale-logo')}
                  />
                  <span className={cx('menu-label')}>{item.title}</span>
                </a>
              )
            }

            return (
              <a
                key={item.id}
                href={item.url}
                onClick={handleClick}
                className={cx('menu-item', item.className, {
                  'highlighted-item': item.isHighlighted,
                })}
                data-elm-id={item.elmId}
              >
                {item.icon && (
                  <Icon
                    name={item.icon}
                    iconStyle={item.iconStyle}
                    className={cx('menu-icon', {
                      'highlighted-icon': item.isHighlighted,
                    })}
                  />
                )}
                <span
                  className={cx('menu-label', {
                    'highlighted-label': item.isHighlighted,
                  })}
                >
                  {!indicatorsEnabled &&
                    item.id === 'notifications' &&
                    formattedNotificationCount && (
                      <span
                        className={hcx('bell-notification')}
                        data-elm-id='mobile_notification_count'
                        style={{ marginRight: '3px' }}
                      >
                        {formattedNotificationCount}
                      </span>
                    )}
                  {item.title}
                  {indicatorsEnabled &&
                    (item.showIndicator ||
                      (item.id === 'notifications' &&
                        !!formattedNotificationCount)) && (
                      <span
                        className={cx('menu-indicator-dot')}
                        data-elm-id={`${item.elmId}_indicator`}
                      />
                    )}
                </span>
              </a>
            )
          })
        })()}
      </div>
    </EnhancedDrawer>
  )
}

export default NavigationDrawer
